/* Sweet Crème — sticky add-to-cart bar (html designs/home/product-macarons-v7).
   Proxies the real product form: reveals after the main buy buttons scroll
   above the viewport, mirrors the submit button's disabled state (covers
   sold-out, unavailable, BYO flavor gating, and in-flight submits), and keeps
   quantity in two-way sync with Dawn's quantity-input. */

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.mainQtyInput = document.getElementById(`Quantity-${this.sectionId}`);
        if (!this.mainSubmit) return;

        this.submitButton = this.querySelector('[data-satc-submit]');
        this.submitLabel = this.querySelector('[data-satc-label]');
        this.variantLabel = this.querySelector('[data-satc-variant]');
        this.qtyValue = this.querySelector('[data-satc-val]');
        this.decButton = this.querySelector('[data-satc-dec]');
        this.incButton = this.querySelector('[data-satc-inc]');

        // Reveal only once the buy-buttons block has scrolled above the viewport.
        this.revealObserver = new IntersectionObserver(
          ([entry]) => this.toggleVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0),
          { threshold: 0 }
        );
        this.revealObserver.observe(this.mainSubmit.closest('.product-form__buttons') || this.mainSubmit);

        // The rendered submit button is the canonical state — mirroring its
        // attributes catches paths that never publish variantChange
        // (product-info setUnavailable) and sweet-creme-byo gating.
        this.disabledObserver = new MutationObserver(() => this.syncSubmitState());
        this.disabledObserver.observe(this.mainSubmit, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });
        this.syncSubmitState();

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data.sectionId !== this.sectionId) return;
          if (this.variantLabel && event.data.variant) this.variantLabel.textContent = event.data.variant.title;
        });
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.syncQuantity());

        this.onMainQtyChange = () => this.syncQuantity();
        this.mainQtyInput?.addEventListener('change', this.onMainQtyChange);
        this.decButton?.addEventListener('click', () => this.bumpQuantity(-1));
        this.incButton?.addEventListener('click', () => this.bumpQuantity(1));
        this.syncQuantity();

        this.submitButton.addEventListener('click', () => this.mainSubmit.click());
      }

      disconnectedCallback() {
        this.revealObserver?.disconnect();
        this.disabledObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        this.mainQtyInput?.removeEventListener('change', this.onMainQtyChange);
        document.documentElement.classList.remove('satc-active');
      }

      toggleVisible(show) {
        if (this.classList.contains('is-visible') === show) return;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', show ? 'false' : 'true');
        this.toggleAttribute('inert', !show);
        document.documentElement.classList.toggle('satc-active', show);
        if (show) document.documentElement.style.setProperty('--satc-h', `${this.offsetHeight}px`);
      }

      bumpQuantity(delta) {
        if (!this.mainQtyInput) return;
        const min = parseInt(this.mainQtyInput.min, 10) || 1;
        const max = parseInt(this.mainQtyInput.max, 10) || Number.POSITIVE_INFINITY;
        const step = parseInt(this.mainQtyInput.step, 10) || 1;
        const current = parseInt(this.mainQtyInput.value, 10) || min;
        const next = Math.min(Math.max(current + delta * step, min), max);
        if (next === current) return;
        // Set the value then fire a bubbling change — the same pattern Dawn's
        // quantity-input buttons use, so validateQtyRules/price-per-item rerun.
        this.mainQtyInput.value = next;
        this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncQuantity() {
        if (!this.mainQtyInput || !this.qtyValue) return;
        const min = parseInt(this.mainQtyInput.min, 10) || 1;
        const max = parseInt(this.mainQtyInput.max, 10);
        const value = parseInt(this.mainQtyInput.value, 10) || min;
        this.qtyValue.textContent = value;
        if (this.decButton) this.decButton.disabled = value <= min;
        if (this.incButton) this.incButton.disabled = Boolean(max) && value >= max;
      }

      syncSubmitState() {
        const disabled =
          this.mainSubmit.hasAttribute('disabled') || this.mainSubmit.getAttribute('aria-disabled') === 'true';
        this.submitButton.disabled = disabled;
        const mainLabel = this.mainSubmit.querySelector('span');
        if (mainLabel && this.submitLabel) this.submitLabel.textContent = mainLabel.textContent.trim();
      }
    }
  );
}
